import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Modal, Select, Table, Tag, message } from "antd";
import {
  CheckCircle2,
  MoreVertical,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plus,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import {
  approvePolicyAgentless,
  listADPolicies,
  policyAction,
  revokePolicyAgentless,
} from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import { StatusPill } from "../ad-policies/adPolicyTokens";
import { StatusPill as TonePill } from "../../../design-system/tableUi";
import {
  dbPolicyFlowLabel,
  dbPolicyFlowTone,
  normalizeDbPolicyFlow,
  UNRECOGNISED_FLOW_NOTICE,
} from "./dbPolicyModel";

function formatUpdated(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "—";
  }
}

const DRAFT_KEYS = ["policyDraft.basicPolicyInformation", "editPolicyData", "formSessionData"];
const clearPolicyDrafts = () => DRAFT_KEYS.forEach((k) => localStorage.removeItem(k));

export default function DbPoliciesPage() {
  const navigate = useNavigate();
  const [messageApi, messageContext] = message.useMessage();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [databaseFilter, setDatabaseFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [modalApi, modalContext] = Modal.useModal();

  const fetchPolicies = useCallback(() => {
    setLoading(true);
    listADPolicies({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        policyType: "database",
        page: 1,
        limit: 200,
      },
      onSuccess: (res) => {
        const list = (res?.policies || res?.data || []).filter(
          (p) => (p?.policyType || "").toLowerCase() === "database"
        );
        setPolicies(list);
        setLoading(false);
      },
      onFailure: () => {
        messageApi.error("Failed to load database policies");
        setLoading(false);
      },
    });
  }, [messageApi]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const databaseNames = useMemo(() => {
    const names = new Set();
    policies.forEach((p) => {
      const name = p?.policyJson?.database?.database_name;
      if (name) names.add(name);
    });
    return [...names];
  }, [policies]);

  const visiblePolicies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      const db = p?.policyJson?.database || {};
      if (q && !(p.policyName || "").toLowerCase().includes(q)) return false;
      if (databaseFilter && db.database_name !== databaseFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      return true;
    });
  }, [policies, search, databaseFilter, statusFilter]);

  const appliedFilters = [
    databaseFilter && {
      key: "database",
      label: `Database · ${databaseFilter}`,
      clear: () => setDatabaseFilter(null),
    },
    statusFilter && { key: "status", label: `Status · ${statusFilter}`, clear: () => setStatusFilter(null) },
  ].filter(Boolean);

  const clearFilters = () => {
    setDatabaseFilter(null);
    setStatusFilter(null);
    setSearch("");
  };

  const handleCreatePolicy = () => {
    clearPolicyDrafts();
    navigate("/pam/policy/v2/createPolicy");
  };

  const handleUpdatePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot edit");
      return;
    }
    clearPolicyDrafts();
    // The database policy form prefills from these keys (see databasePolicy.jsx).
    localStorage.setItem("editPolicyData", JSON.stringify(row));
    localStorage.setItem("policyDraft.basicPolicyInformation", JSON.stringify(row));
    navigate("/pam/policy/v2/createPolicy?edit=true", { state: { policy: row } });
  };

  const handleDeletePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot delete");
      return;
    }
    modalApi.confirm({
      title: `Delete policy "${row.policyName}"?`,
      content: "This cannot be undone. The policy will stop being evaluated immediately.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          deletePolicyJSON({
            payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" deleted`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to delete policy");
              resolve();
            },
          });
        }),
    });
  };

  const handleChangeStatus = (row, actionValue) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    const successMsg = actionValue === "Approved" ? "Policy re-activated" : "Policy paused";
    const failureVerb = actionValue === "Approved" ? "re-activate" : "pause";
    policyAction({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        action: { actionType: "status", actionIds: [id], actionValue },
      },
      onSuccess: () => {
        messageApi.success(successMsg);
        fetchPolicies();
      },
      onFailure: () => messageApi.error(`Failed to ${failureVerb} policy`),
    });
  };

  const handleApprovePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    approvePolicyAgentless({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
      onSuccess: () => {
        messageApi.success(`Policy "${row.policyName}" approved`);
        fetchPolicies();
      },
      onFailure: () => messageApi.error("Failed to approve policy"),
    });
  };

  const handleRevokePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    modalApi.confirm({
      title: `Revoke policy "${row.policyName}"?`,
      content: "The policy will stop being evaluated until it's approved again.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          revokePolicyAgentless({
            payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" revoked`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to revoke policy");
              resolve();
            },
          });
        }),
    });
  };

  const columns = [
    {
      title: "Policy name",
      key: "policyName",
      render: (_, row) => (
        <span style={{ fontFamily: AN_FONT, fontWeight: 500, color: AN_COLORS.ink }}>
          {row.policyName}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_, row) => {
        const flow = row.policyJson?.policyFlow;
        const unrecognised = normalizeDbPolicyFlow(flow) === null;
        return (
          <span title={unrecognised ? UNRECOGNISED_FLOW_NOTICE : undefined}>
            <TonePill label={dbPolicyFlowLabel(flow)} tone={dbPolicyFlowTone(flow)} />
          </span>
        );
      },
    },
    {
      title: "Database",
      key: "database",
      render: (_, row) => {
        const db = row.policyJson?.database || {};
        return (
          <div>
            <div style={{ fontWeight: 500, color: AN_COLORS.ink }}>{db.database_name || "—"}</div>
            {db.host && (
              <div style={{ fontFamily: AN_MONO, fontSize: 11.5, color: AN_COLORS.muted }}>
                {db.host}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "DB user",
      key: "dbUser",
      width: 130,
      render: (_, row) => {
        const user = row.policyJson?.database?.user;
        return user ? (
          <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{user}</span>
        ) : (
          <span style={{ color: AN_COLORS.muted }}>—</span>
        );
      },
    },
    {
      title: "Privileges",
      key: "privileges",
      width: 170,
      render: (_, row) => {
        const privileges = row.policyJson?.database?.privilege || [];
        if (!privileges.length) return <span style={{ color: AN_COLORS.muted }}>—</span>;
        return (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {privileges.map((p) => (
              <Tag key={p} color="geekblue" style={{ marginInlineEnd: 0 }}>
                {p}
              </Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: "Assigned to",
      key: "assignedTo",
      render: (_, row) => {
        const users = row.policyJson?.permissions?.iam_users || [];
        if (!users.length) return <span style={{ color: AN_COLORS.muted }}>—</span>;
        const [first, ...rest] = users;
        return (
          <span style={{ fontSize: 12.5 }}>
            {first}
            {rest.length > 0 && (
              <span style={{ color: AN_COLORS.muted }}> +{rest.length} more</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, row) => <StatusPill status={row.status} />,
    },
    {
      title: "Updated",
      key: "updated",
      width: 170,
      render: (_, row) => (
        <span
          style={{
            fontFamily: AN_MONO,
            fontSize: 12,
            color: AN_COLORS.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatUpdated(row.updatedAt)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 44,
      align: "center",
      render: (_, row) => {
        const isApproved = row.status === "Approved";
        const items = [
          {
            key: "update",
            icon: <Pencil size={13} />,
            label: "Update policy",
            onClick: () => handleUpdatePolicy(row),
          },
        ];
        if (!isApproved) {
          items.push({
            key: "approve",
            icon: <CheckCircle2 size={13} />,
            label: "Approve",
            onClick: () => handleApprovePolicy(row),
          });
        }
        if (isApproved) {
          items.push({
            key: "pause",
            icon: <PauseCircle size={13} />,
            label: "Pause",
            onClick: () => handleChangeStatus(row, "Suspended"),
          });
        } else if (row.status === "Suspended") {
          items.push({
            key: "reactivate",
            icon: <PlayCircle size={13} />,
            label: "Re-activate",
            onClick: () => handleChangeStatus(row, "Approved"),
          });
        }
        if (isApproved) {
          items.push({
            key: "revoke",
            icon: <XCircle size={13} />,
            label: "Revoke",
            onClick: () => handleRevokePolicy(row),
          });
        }
        items.push({ type: "divider" });
        items.push({
          key: "delete",
          danger: true,
          icon: <Trash2 size={13} />,
          label: "Delete policy",
          onClick: () => handleDeletePolicy(row),
        });
        return (
          <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }}>
            <button
              type="button"
              aria-label="Row actions"
              style={{
                width: 28,
                height: 28,
                border: "none",
                background: "transparent",
                color: AN_COLORS.muted,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 6,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "22px 28px",
      }}
    >
      {messageContext}
      {modalContext}

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Admin · Policies · Database
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
            Database Policies
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            Grant users access to databases, tables, and privileges with MFA enforcement.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <CountChip
              count={policies.length}
              label={policies.length === 1 ? "Policy" : "Policies"}
            />
            {databaseNames.length > 0 && (
              <CountChip
                count={databaseNames.length}
                label={databaseNames.length === 1 ? "Database" : "Databases"}
              />
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            icon={<Plus size={13} strokeWidth={2.2} />}
            onClick={handleCreatePolicy}
          >
            New policy
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div
        style={{
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.6fr 1fr 1fr",
            gap: 12,
            alignItems: "end",
          }}
        >
          <FilterField label="Search by name">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<Search size={14} color={AN_COLORS.muted} />}
              placeholder="db-1234567-…"
              style={{ height: 36 }}
            />
          </FilterField>
          <FilterField label="Database">
            <Select
              value={databaseFilter}
              onChange={setDatabaseFilter}
              allowClear
              showSearch
              placeholder="Any database"
              style={{ width: "100%", height: 36 }}
              options={databaseNames.map((name) => ({ value: name, label: name }))}
            />
          </FilterField>
          <FilterField label="Status">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              placeholder="Any status"
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "Approved", label: "Active" },
                { value: "Suspended", label: "Paused" },
              ]}
            />
          </FilterField>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontWeight: 500 }}>Applied:</span>
          {appliedFilters.length === 0 ? (
            <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>None</span>
          ) : (
            appliedFilters.map((f) => (
              <AppliedChip key={f.key} onClear={f.clear}>
                {f.label}
              </AppliedChip>
            ))
          )}
          {appliedFilters.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                border: "none",
                background: "transparent",
                color: AN_COLORS.primary,
                fontSize: 12.5,
                fontWeight: 500,
                cursor: "pointer",
                padding: 0,
              }}
            >
              Clear filters
            </button>
          )}
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: AN_COLORS.muted,
              fontFamily: AN_MONO,
            }}
          >
            Showing {visiblePolicies.length} of {policies.length}
          </span>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Table
          rowKey={(row) => row.id || row.policyId || row.policyName}
          columns={columns}
          dataSource={visiblePolicies}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: loading ? "Loading…" : "No database policies yet." }}
        />
      </div>
    </div>
  );
}

function CountChip({ count, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 6,
        padding: "3px 10px",
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: 99,
        fontSize: 12.5,
        color: AN_COLORS.muted,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          fontFamily: AN_MONO,
          fontWeight: 600,
          color: AN_COLORS.ink,
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
        }}
      >
        {count.toLocaleString()}
      </span>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </span>
  );
}

function FilterField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 500,
          color: AN_COLORS.ink2,
          marginBottom: 6,
          fontFamily: AN_FONT,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function AppliedChip({ children, onClear }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 4px 4px 10px",
        background: AN_COLORS.primaryL,
        color: AN_COLORS.primary,
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 500,
        border: `1px solid ${anAlpha(AN_COLORS.primary, 13)}`,
      }}
    >
      {children}
      <button
        type="button"
        onClick={onClear}
        aria-label="Remove filter"
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: "none",
          background: "transparent",
          color: AN_COLORS.primary,
          cursor: "pointer",
          padding: 0,
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </span>
  );
}
